import type { FC } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MDText } from 'i18n-react';

import { ArrowLeftIcon, ArrowRightIcon, Button } from '@motorway/mw-highway-code';

import TextLink from 'Components/shared/TextLink/TextLink';
import { useSeenFilter } from 'Components/VehicleList/SeenFilter';
import { useHasFilters } from 'Components/VehicleList/VehicleListV2/FilterBarV2/hooks/useHasFilters';
import { useSeenFilterExperimentation } from 'Services/optimizely/experimentations/useSeenFilterExperimentation';
import { useFilters } from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { useResetFilters } from 'Utilities/hooks/vehicleList/useResetFilters';
import { VIEWING_FILTER } from 'Utilities/seenVehicles/seenVehicles';

import texts from '../VehicleListV2.texts.json';

import { AllFiltersButton } from './filterChips/AllFiltersButton';
import { FilterChipRenderer } from './filterChips/FilterChipRenderer';
import { FilterBarScrollContext, useStickyFilterBar } from './hooks/useStickyFilterBar';
import { AllFiltersModal } from './AllFiltersModal';
import { FilterBarStart } from './FilterBarStart';
import { FILTER_CHIP_CONFIG } from './FilterBarV2.consts';
import { getModalOnlyFilterCount } from './FilterBarV2.helpers';

import styles from './FilterBarV2.module.scss';

const LocalT = new MDText(texts);

interface FilterBarV2Props {
	isLoading?: boolean;
}

export const FilterBarV2: FC<FilterBarV2Props> = ({ isLoading = false }) => {
	const filters = useFilters();
	const resetFilters = useResetFilters();
	const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);
	const barRef = useRef<HTMLDivElement>(null);
	const chipsInnerRef = useRef<HTMLDivElement>(null);
	const sentinelRef = useRef<HTMLDivElement>(null);

	const { scrollToStartOfStickyBar } = useStickyFilterBar({ barRef, sentinelRef });

	const hasActiveFilters = useHasFilters();
	const [seenFilter] = useSeenFilter();
	const isSeenFilterEnabled = useSeenFilterExperimentation();
	const seenFilterCount = isSeenFilterEnabled && seenFilter !== VIEWING_FILTER.ALL ? 1 : 0;
	const modalOnlyFilterCount = getModalOnlyFilterCount(filters) + seenFilterCount;
	const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
	const [isScrolledToStart, setIsScrolledToStart] = useState(true);

	const handleScroll = useCallback(() => {
		const el = chipsInnerRef.current;
		if (!el) return;
		setIsScrolledToEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
		setIsScrolledToStart(el.scrollLeft <= 1);
	}, []);

	useEffect(() => {
		const el = chipsInnerRef.current;
		if (!el) return undefined;

		const observer = new ResizeObserver(() => handleScroll());
		observer.observe(el);

		return () => observer.disconnect();
	}, [handleScroll]);

	const handleScrollLeft = useCallback(() => {
		const el = chipsInnerRef.current;
		if (el) el.scrollBy({ behavior: 'smooth', left: -el.clientWidth });
	}, []);

	const handleScrollRight = useCallback(() => {
		const el = chipsInnerRef.current;
		if (el) el.scrollBy({ behavior: 'smooth', left: el.clientWidth });
	}, []);

	return (
		<FilterBarScrollContext.Provider value={scrollToStartOfStickyBar}>
			<div ref={sentinelRef} className={styles.sentinel} />

			<div ref={barRef} className={styles.bar} data-filter-bar="v2" data-testid="filter-bar-v2">
				<div
					ref={chipsInnerRef}
					className={styles.chipsInner}
					data-scroll-end={isScrolledToEnd || undefined}
					data-scroll-start={isScrolledToStart || undefined}
					onScroll={handleScroll}
				>
					<FilterBarStart />

					<div className={styles.railFilters} data-testid="filter-bar-rail-filters">
						{FILTER_CHIP_CONFIG.map((chipConfig) => (
							<FilterChipRenderer key={chipConfig.id} chipConfig={chipConfig} />
						))}

						<AllFiltersButton activeFilterCount={modalOnlyFilterCount} onClick={() => setIsAllFiltersOpen(true)} />
					</div>

					<div className={styles.hiddenWhenSticky}>
						{hasActiveFilters && (
							<TextLink onClick={() => resetFilters()} text={`${LocalT.translate('filterBar.clearAll')}`} />
						)}
					</div>
				</div>

				<div className={styles.scrollControls}>
					<Button
						disabled={isScrolledToStart}
						icon={ArrowLeftIcon}
						label={`${LocalT.translate('filterBar.scrollLeft')}`}
						onClick={handleScrollLeft}
						showLabel={false}
						size="small"
						variant="secondary"
					/>
					<Button
						disabled={isScrolledToEnd}
						icon={ArrowRightIcon}
						label={`${LocalT.translate('filterBar.scrollRight')}`}
						onClick={handleScrollRight}
						showLabel={false}
						size="small"
						variant="secondary"
					/>
				</div>
			</div>

			{isAllFiltersOpen && (
				<AllFiltersModal isLoading={isLoading} isOpen={isAllFiltersOpen} onClose={() => setIsAllFiltersOpen(false)} />
			)}
		</FilterBarScrollContext.Provider>
	);
};
